/**
 * Typed FastAPI client for the PUBLIC /lunar endpoints.
 *
 * Unlike most TMA reads, `GET /lunar/today` and `GET /lunar/upcoming` are
 * unauthenticated (services/api/.../routers/lunar.py) — they take a single
 * `tz` IANA query param and return a stateless Swiss-Ephemeris snapshot. So
 * there is NO session bootstrap here (no `ensureSessionToken`, no birth data).
 *
 * Request shape (verbatim from the router):
 *   GET /lunar/today?tz=<iana>          → { moment, tz, phase, sign, sign_degree, void_of_course? }
 *   GET /lunar/upcoming?count=5&tz=<iana> → { tz, events: [{ kind, moment, sign }] }
 *
 * Day 1-3: MOCK_MODE returns local fixtures (no network). Day 4+: VITE_MOCK_API=0.
 */
import { z } from "zod";

import { apiFetch, MOCK_MODE } from "@/lib/api";

/** Resolve the device IANA timezone (e.g. "Europe/Moscow"); falls back to UTC. */
export function resolveTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/* ----- Schemas (mirror services/api/.../schemas/lunar.py) ----- */

export const phaseNameSchema = z.enum([
  "new",
  "waxing-crescent",
  "first-quarter",
  "waxing-gibbous",
  "full",
  "waning-gibbous",
  "last-quarter",
  "balsamic",
]);
export type PhaseName = z.infer<typeof phaseNameSchema>;

const phaseSchema = z.object({
  name: phaseNameSchema,
  elongation_deg: z.number(),
  illumination_pct: z.number(),
  age_days: z.number(),
  is_waxing: z.boolean(),
});

const voidOfCourseSchema = z.object({
  start: z.string(),
  end: z.string(),
  sign_at_start: z.string(),
  next_sign: z.string(),
  is_active: z.boolean(),
});

export const lunarTodaySchema = z.object({
  moment: z.string(),
  tz: z.string(),
  phase: phaseSchema,
  sign: z.string(),
  sign_degree: z.number(),
  moon_longitude: z.number(),
  sun_longitude: z.number(),
  void_of_course: voidOfCourseSchema.nullable().optional(),
});
export type LunarToday = z.infer<typeof lunarTodaySchema>;

export const eventKindSchema = z.enum([
  "new-moon",
  "full-moon",
  "solar-eclipse",
  "lunar-eclipse",
  "super-moon",
]);
export type EventKind = z.infer<typeof eventKindSchema>;

const lunarEventSchema = z.object({
  kind: eventKindSchema,
  moment: z.string(),
  sign: z.string(),
});
export type LunarEvent = z.infer<typeof lunarEventSchema>;

export const lunarUpcomingSchema = z.object({
  tz: z.string(),
  events: z.array(lunarEventSchema),
});
export type LunarUpcoming = z.infer<typeof lunarUpcomingSchema>;

/* ----- Mock fixtures (Day 1-3) — match server contract verbatim ----- */

// The server returns `sign` / `phase.name` / `kind` as ASCII slugs
// (services/api/.../domain/lunar/phase.py — e.g. "virgo", "waxing-gibbous").
// Fixtures mirror that verbatim so a switch to real fetch is a one-flag flip;
// the RU labels are applied in the components, exactly like real responses.
const MOCK_TODAY: LunarToday = {
  moment: "2026-05-11T09:00:00+03:00",
  tz: "Europe/Moscow",
  phase: {
    name: "waxing-gibbous",
    elongation_deg: 132.4,
    illumination_pct: 71.3,
    age_days: 9.8,
    is_waxing: true,
  },
  sign: "virgo",
  sign_degree: 14.37,
  moon_longitude: 164.37,
  sun_longitude: 51.2,
  void_of_course: {
    start: "2026-05-11T18:42:00+03:00",
    end: "2026-05-12T03:10:00+03:00",
    sign_at_start: "virgo",
    next_sign: "libra",
    is_active: false,
  },
};

const MOCK_UPCOMING: LunarUpcoming = {
  tz: "Europe/Moscow",
  events: [
    { kind: "full-moon", moment: "2026-05-12T19:56:00+03:00", sign: "scorpio" },
    { kind: "new-moon", moment: "2026-05-27T06:02:00+03:00", sign: "gemini" },
    { kind: "full-moon", moment: "2026-06-11T10:44:00+03:00", sign: "sagittarius" },
    { kind: "super-moon", moment: "2026-06-11T11:10:00+03:00", sign: "sagittarius" },
    { kind: "new-moon", moment: "2026-06-25T13:31:00+03:00", sign: "cancer" },
  ],
};

function delay<T>(value: T, ms = 120): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

/* ----- Typed endpoint helpers ----- */

export async function fetchLunarToday(tz: string): Promise<LunarToday> {
  if (MOCK_MODE) return delay(MOCK_TODAY);
  return apiFetch({
    path: `/lunar/today?tz=${encodeURIComponent(tz)}`,
    schema: lunarTodaySchema,
  });
}

export async function fetchLunarUpcoming(tz: string, count = 5): Promise<LunarUpcoming> {
  if (MOCK_MODE) return delay(MOCK_UPCOMING);
  return apiFetch({
    path: `/lunar/upcoming?count=${count}&tz=${encodeURIComponent(tz)}`,
    schema: lunarUpcomingSchema,
  });
}
